import { Address4, Address6 } from 'ip-address'
import { z } from 'zod'

export type IpVersion = 4 | 6

export interface ParsedCidr {
  version: IpVersion
  // Canonical network string (host bits zeroed): e.g. ``"203.0.113.0/24"``.
  canonical: string
}

const v4Cidr = z.cidrv4()
const v6Cidr = z.cidrv6()
const v4Ip = z.ipv4()
const v6Ip = z.ipv6()

const WILDCARD_V4 = '0.0.0.0/0'
const WILDCARD_V6 = '::/0'

export const parseCidr = (input: string): ParsedCidr | { error: string } => {
  const trimmed = input.trim()
  if (trimmed.length === 0) return { error: 'CIDR entry cannot be empty' }

  // zod validates syntax first; the ``ip-address`` constructors only run on
  // already-valid input so neither needs a try/catch.
  if (v4Cidr.safeParse(trimmed).success) {
    const addr = new Address4(trimmed)
    const canonical = `${addr.startAddress().correctForm()}/${addr.subnetMask}`
    if (canonical === WILDCARD_V4) {
      return { error: `Wildcard ${WILDCARD_V4} is not allowed — leave the list empty to disable the feature` }
    }
    return { version: 4, canonical }
  }

  if (v6Cidr.safeParse(trimmed).success) {
    const addr = new Address6(trimmed)
    const canonical = `${addr.startAddress().correctForm()}/${addr.subnetMask}`
    if (canonical === WILDCARD_V6) {
      return { error: `Wildcard ${WILDCARD_V6} is not allowed — leave the list empty to disable the feature` }
    }
    return { version: 6, canonical }
  }

  return { error: `Invalid CIDR "${trimmed}" — expected a.b.c.d/n or v6 form` }
}

export interface ValidatedAllowlist {
  cidrs: string[]
  errors: string[]
}

// Splits a free-form textarea on commas, newlines, or whitespace, parses each
// non-empty token, and returns the canonicalized list along with one error per
// bad token. Duplicates after canonicalization are collapsed (preserve order).
export const parseAllowlistInput = (raw: string): ValidatedAllowlist => {
  const tokens = raw.split(/[\s,]+/).filter((t) => t.length > 0)
  const errors: string[] = []
  const cidrs: string[] = []
  const seen = new Set<string>()
  for (const token of tokens) {
    const result = parseCidr(token)
    if ('error' in result) {
      errors.push(result.error)
      continue
    }
    if (seen.has(result.canonical)) continue
    seen.add(result.canonical)
    cidrs.push(result.canonical)
  }
  return { cidrs, errors }
}

// Returns true iff ``ip`` (a single address, no prefix) is contained in any of
// the canonical CIDRs. Cross-family entries are skipped; IPv4-mapped IPv6
// (``::ffff:a.b.c.d``) is unwrapped so the v4 CIDR check still applies.
export const ipInAnyCidr = (ip: string, cidrs: string[]): boolean => {
  const trimmed = ip.trim()
  if (trimmed.length === 0) return false

  const isV4 = v4Ip.safeParse(trimmed).success
  const isV6 = !isV4 && v6Ip.safeParse(trimmed).success
  if (!isV4 && !isV6) return false

  // For IPv4-mapped IPv6, unwrap to v4 so v4 CIDRs match.
  const v6Probe = isV6 ? new Address6(trimmed) : null
  const v4Probe = isV4 ? new Address4(trimmed) : v6Probe?.is4() ? new Address4(v6Probe.to4().correctForm()) : null

  for (const cidr of cidrs) {
    if (v4Probe && v4Cidr.safeParse(cidr).success) {
      if (v4Probe.isInSubnet(new Address4(cidr))) return true
    } else if (v6Probe && v6Cidr.safeParse(cidr).success) {
      if (v6Probe.isInSubnet(new Address6(cidr))) return true
    }
  }
  return false
}
